import React, { useState, useEffect, useMemo, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  ShoppingCart,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react"
import { Dialog } from "@mui/material"
import { useWebsiteProduct } from "@/hooks/useWebsiteProduct"
import { useSimilarProducts } from "@/hooks/useSimilarProducts"
import { useCart } from "@/hooks/useCart"
import { toast } from "sonner"
import { formatToDevanagariStyle } from "@/utilities/helper"
import ProductGroup from "../components/ProductGroup"

const getImageUrl = (image: any) => {
  if (!image) {
    return ""
  }

  return image.url || image.path || image
}

const getVariantImageUrls = (variant: any) => {
  if (variant?.images && variant.images.length > 0) {
    return variant.images.map((img: any) => getImageUrl(img)).filter(Boolean)
  }

  if (variant?.thumbnail) {
    const url = getImageUrl(variant.thumbnail)
    return url ? [url] : []
  }

  return []
}

// const getGroupDefaultImageUrl = (product: any) => {
//   return (
//     getImageUrl(product?.thumbnail) ||
//     (product?.images && product.images.length > 0
//       ? getImageUrl(product.images[0])
//       : "") ||
//     getImageUrl(product?.product_group?.image) ||
//     getImageUrl(product?.product_group?.thumbnail) ||
//     ""
//   )
// }

const buildGalleryImages = (product: any, variants: any[]) => {
  const uniqueImages = new Map<string, { url: string; variantId?: number }>()
  const groupUrls: string[] = []

  if (product?.images && product.images.length > 0) {
    product.images.forEach((image: any) => {
      const url = getImageUrl(image)
      if (url) {
        groupUrls.push(url)
      }
    })
  }

  if (product?.thumbnail) {
    const url = getImageUrl(product.thumbnail)
    if (url) {
      groupUrls.push(url)
    }
  }

  if (product?.product_group?.image) {
    const url = getImageUrl(product.product_group.image)
    if (url) {
      groupUrls.push(url)
    }
  }

  if (product?.product_group?.thumbnail) {
    const url = getImageUrl(product.product_group.thumbnail)
    if (url) {
      groupUrls.push(url)
    }
  }

  groupUrls.forEach((url) => {
    if (!uniqueImages.has(url)) {
      uniqueImages.set(url, { url })
    }
  })

  variants.forEach((variant) => {
    getVariantImageUrls(variant).forEach((url: any) => {
      const existing = uniqueImages.get(url)
      if (!existing || !existing.variantId) {
        uniqueImages.set(url, { url, variantId: variant.id })
      }
    })
  })

  return Array.from(uniqueImages.values())
}

// const getProductSingleImage = (product: any, rootProduct?: any) => {
//   if (product?.thumbnail) {
//     const url = getImageUrl(product.thumbnail)
//     if (url) {
//       return url
//     }
//   }

//   if (product?.images && product.images.length > 0) {
//     const url = getImageUrl(product.images[0])
//     if (url) {
//       return url
//     }
//   }

//   const productGroupImage =
//     getImageUrl(product?.product_group?.image) ||
//     getImageUrl(product?.product_group?.thumbnail)

//   if (productGroupImage) {
//     return productGroupImage
//   }

//   return (
//     getImageUrl(rootProduct?.thumbnail) ||
//     (rootProduct?.images && rootProduct.images.length > 0
//       ? getImageUrl(rootProduct.images[0])
//       : "") ||
//     getImageUrl(rootProduct?.product_group?.image) ||
//     getImageUrl(rootProduct?.product_group?.thumbnail) ||
//     ""
//   )
// }

export const ProductDetail: React.FC = () => {
  const { sku } = useParams<{ sku: string }>()
  const navigate = useNavigate()
  const productSku = sku ? sku : ""

  const { data: productData, isLoading } = useWebsiteProduct(productSku)
  const { data: similarProductsData, isLoading: isSimilarLoading } =
    useSimilarProducts(productSku)

  const { addItem, isAdding } = useCart()
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState<
    number | null
  >(null)
  const [quantity, setQuantity] = useState(1)
  const [selectedVariant, setSelectedVariant] = useState<any>(null)
  const [selectedColor, setSelectedColor] = useState<string>("")
  const [selectedSize, setSelectedSize] = useState<string>("")

  const thumbnailStripRef = useRef<HTMLDivElement>(null)

  const product = (productData as any)?.data?.data || (productData as any)?.data
  const similarProducts =
    (similarProductsData as any)?.data?.data ||
    (similarProductsData as any)?.data ||
    []

  const token = localStorage.getItem("website_token")
  const selectedVariantAvailableStock = Number(
    selectedVariant?.available_stock ?? selectedVariant?.stock ?? 0
  )

  // Extract variants
  const variants = product?.products || []

  // Extract unique colors and sizes
  const colors = variants.reduce((acc: string[], p: any) => {
    const attr = p.product_attributes.find((a: any) => a.key === "color")
    if (attr?.attribute && !acc.includes(attr.attribute))
      acc.push(attr.attribute)
    return acc
  }, [])

  const sizes = variants.reduce((acc: string[], p: any) => {
    const attr = p.product_attributes.find((a: any) => a.key === "size")
    if (attr?.attribute && !acc.includes(attr.attribute))
      acc.push(attr.attribute)
    return acc
  }, [])

  useEffect(() => {
    setQuantity((currentQuantity) =>
      Math.min(currentQuantity, Math.max(1, selectedVariantAvailableStock))
    )
  }, [selectedVariant?.id, selectedVariantAvailableStock])

  // Reset page-specific state before initializing a variant for the next group.
  // This must run before the initialization effect below: cached React Query data
  // can already be available when this component mounts again for the same SKU.
  useEffect(() => {
    setSelectedImageIndex(0)
    setQuantity(1)
    setSelectedVariant(null)
    setSelectedColor("")
    setSelectedSize("")
    window.scrollTo(0, 0)
  }, [sku])

  // Always select a sellable product from the current product group by default.
  useEffect(() => {
    const defaultVariant = variants[0]

    if (!defaultVariant) {
      return
    }

    const selectedVariantBelongsToGroup = variants.some(
      (variant: any) => variant.id === selectedVariant?.id
    )

    if (selectedVariantBelongsToGroup) {
      return
    }

    setSelectedVariant(defaultVariant)
    const colorAttr = defaultVariant.product_attributes.find(
      (a: any) => a.key === "color"
    )
    const sizeAttr = defaultVariant.product_attributes.find(
      (a: any) => a.key === "size"
    )
    setSelectedColor(colorAttr?.attribute || "")
    setSelectedSize(sizeAttr?.attribute || "")
  }, [selectedVariant?.id, variants])

  // Update selected variant when color or size changes
  useEffect(() => {
    if (selectedColor && selectedSize) {
      const matchingVariant = variants.find((p: any) => {
        const colorAttr = p.product_attributes.find(
          (a: any) => a.key === "color"
        )
        const sizeAttr = p.product_attributes.find((a: any) => a.key === "size")
        return (
          colorAttr?.attribute === selectedColor &&
          sizeAttr?.attribute === selectedSize
        )
      })
      if (matchingVariant) {
        setSelectedVariant(matchingVariant)
      }
    } else if (selectedColor) {
      const matchingVariant = variants.find((p: any) => {
        const colorAttr = p.product_attributes.find(
          (a: any) => a.key === "color"
        )
        return colorAttr?.attribute === selectedColor
      })
      if (matchingVariant) {
        setSelectedVariant(matchingVariant)
        const sizeAttr = matchingVariant.product_attributes.find(
          (a: any) => a.key === "size"
        )
        setSelectedSize(sizeAttr?.attribute || "")
      }
    } else if (selectedSize) {
      const matchingVariant = variants.find((p: any) => {
        const sizeAttr = p.product_attributes.find((a: any) => a.key === "size")
        return sizeAttr?.attribute === selectedSize
      })
      if (matchingVariant) {
        setSelectedVariant(matchingVariant)
        const colorAttr = matchingVariant.product_attributes.find(
          (a: any) => a.key === "color"
        )
        setSelectedColor(colorAttr?.attribute || "")
      }
    }
  }, [selectedColor, selectedSize, variants])

  const galleryImages = useMemo(
    () => buildGalleryImages(product, variants),
    [product, variants]
  )

  useEffect(() => {
    setFullScreenImageIndex(null)
  }, [sku])

  const moveFullScreenImage = (direction: number) => {
    setFullScreenImageIndex((current) =>
      current === null || galleryImages.length === 0
        ? null
        : (current + direction + galleryImages.length) % galleryImages.length
    )
  }

  // Scroll active thumbnail into view in the vertical strip
  useEffect(() => {
    if (thumbnailStripRef.current) {
      const activeThumb = thumbnailStripRef.current.querySelector(
        `[data-index="${selectedImageIndex}"]`
      ) as HTMLElement
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: "nearest", behavior: "smooth" })
      }
    }
  }, [selectedImageIndex])

  useEffect(() => {
    if (!selectedVariant || galleryImages.length === 0) {
      return
    }

    const variantImageIndex = galleryImages.findIndex(
      (image) => image.variantId === selectedVariant.id
    )
    const groupImageIndex = galleryImages.findIndex((image) => !image.variantId)

    if (variantImageIndex >= 0) {
      setSelectedImageIndex(variantImageIndex)
      return
    }

    if (groupImageIndex >= 0) {
      setSelectedImageIndex(groupImageIndex)
    }
  }, [selectedVariant, galleryImages])

  const handleImageClick = (index: number) => {
    const image = galleryImages[index]
    if (!image) {
      return
    }

    if (image.variantId) {
      const variant = variants.find((p: any) => p.id === image.variantId)
      if (variant && variant.id !== selectedVariant?.id) {
        setSelectedVariant(variant)
        const colorAttr = variant.product_attributes.find(
          (a: any) => a.key === "color"
        )
        const sizeAttr = variant.product_attributes.find(
          (a: any) => a.key === "size"
        )
        setSelectedColor(colorAttr?.attribute || "")
        setSelectedSize(sizeAttr?.attribute || "")
      }
    }

    setSelectedImageIndex(index)
  }

  const handleAddToCart = () => {
    if (!token) {
      toast.error("Please login to add items to cart")
      navigate("/login")
      return
    }

    if (!selectedVariant) return

    addItem({
      product_id: selectedVariant.id,
      quantity: quantity,
    })
    setQuantity(1) // Reset quantity after adding
  }

  const handlePreviousImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    )
  }

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    )
  }

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen py-10 flex items-center justify-center">
        <div className="text-gray-500">Loading product...</div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="bg-gray-50 min-h-screen py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate("/")}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Products
          </button>
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">Product not found</p>
          </div>
        </div>
      </div>
    )
  }

  const hasMultipleImages = galleryImages.length > 1

  return (
    <div className="bg-gray-50 min-h-screen py-10">
      {/* Styled scrollbar for thumbnail strip */}
      <style>{`
        .thumb-strip::-webkit-scrollbar {
          width: 3px;
        }
        .thumb-strip::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .thumb-strip::-webkit-scrollbar-thumb {
          background: #c7d2fe;
          border-radius: 10px;
        }
        .thumb-strip::-webkit-scrollbar-thumb:hover {
          background: #4f46e5;
        }
        .thumb-strip-mobile::-webkit-scrollbar {
          height: 3px;
        }
        .thumb-strip-mobile::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .thumb-strip-mobile::-webkit-scrollbar-thumb {
          background: #c7d2fe;
          border-radius: 10px;
        }
        .thumb-strip-mobile::-webkit-scrollbar-thumb:hover {
          background: #4f46e5;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate("/")}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Products
        </button>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-12">
          <div className="lg:grid lg:grid-cols-7 lg:gap-x-8">
            {/* Image Gallery */}
            <div className="lg:col-span-3">
              {/* Desktop: vertical thumbnail strip left + main image */}
              <div className="hidden lg:flex gap-3 p-4">
                {/* Vertical thumbnail strip */}
                {hasMultipleImages && (
                  <div
                    ref={thumbnailStripRef}
                    className="thumb-strip flex flex-col gap-2 overflow-y-auto"
                    style={{
                      maxHeight: "480px",
                      width: "76px",
                      minWidth: "76px",
                    }}
                  >
                    {galleryImages.map((image, index) => (
                      <button
                        key={index}
                        data-index={index}
                        onClick={() => handleImageClick(index)}
                        className={`relative flex-shrink-0 w-[72px] h-[72px] rounded-md overflow-hidden border-2 transition-all duration-150 ${
                          selectedImageIndex === index
                            ? "border-indigo-600 shadow-sm"
                            : "border-gray-200 hover:border-indigo-300"
                        }`}
                      >
                        <img
                          src={image.url || ""}
                          alt={`View ${index + 1}`}
                          className="h-full w-full object-cover object-center"
                        />
                        {/* Active indicator dot */}
                        {selectedImageIndex === index && (
                          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Main image */}
                <div className="flex-1 relative group">
                  <div
                    className="w-full overflow-hidden rounded-lg bg-gray-100 relative"
                    style={{ aspectRatio: "1 / 1" }}
                  >
                    <button
                      type="button"
                      aria-label="Open product image gallery"
                      onClick={() =>
                        setFullScreenImageIndex(selectedImageIndex)
                      }
                      disabled={galleryImages.length === 0}
                      className="h-full w-full cursor-zoom-in disabled:cursor-default"
                    >
                      <img
                        src={galleryImages[selectedImageIndex]?.url || ""}
                        alt={selectedVariant?.name || product?.name}
                        className="h-full w-full object-cover object-center transition-opacity duration-200"
                      />
                    </button>

                    {/* Prev / Next arrows — always visible when multiple images */}
                    {hasMultipleImages && (
                      <>
                        <button
                          onClick={handlePreviousImage}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1.5 shadow-sm transition-all hover:shadow-md"
                          aria-label="Previous image"
                        >
                          <ChevronLeft size={18} className="text-gray-700" />
                        </button>
                        <button
                          onClick={handleNextImage}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1.5 shadow-sm transition-all hover:shadow-md"
                          aria-label="Next image"
                        >
                          <ChevronRight size={18} className="text-gray-700" />
                        </button>

                        {/* Image counter pill */}
                        <span className="absolute bottom-3 right-3 bg-black/50 text-white text-xs font-medium px-2 py-1 rounded-full backdrop-blur-sm">
                          {selectedImageIndex + 1} / {galleryImages.length}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile: main image + horizontal thumbnail strip below */}
              <div className="lg:hidden">
                <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                  <button
                    type="button"
                    aria-label="Open product image gallery"
                    onClick={() => setFullScreenImageIndex(selectedImageIndex)}
                    disabled={galleryImages.length === 0}
                    className="h-full w-full cursor-zoom-in disabled:cursor-default"
                  >
                    <img
                      src={galleryImages[selectedImageIndex]?.url || ""}
                      alt={selectedVariant?.name || product?.name}
                      className="h-full w-full object-cover object-center"
                    />
                  </button>
                  {hasMultipleImages && (
                    <>
                      <button
                        onClick={handlePreviousImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 border border-gray-200 rounded-full p-1.5 shadow-sm"
                        aria-label="Previous image"
                      >
                        <ChevronLeft size={18} className="text-gray-700" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 border border-gray-200 rounded-full p-1.5 shadow-sm"
                        aria-label="Next image"
                      >
                        <ChevronRight size={18} className="text-gray-700" />
                      </button>
                      <span className="absolute bottom-3 right-3 bg-black/50 text-white text-xs font-medium px-2 py-1 rounded-full backdrop-blur-sm">
                        {selectedImageIndex + 1} / {galleryImages.length}
                      </span>
                    </>
                  )}
                </div>

                {hasMultipleImages && (
                  <div className="thumb-strip-mobile flex gap-2 overflow-x-auto px-4 py-3">
                    {galleryImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => handleImageClick(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all duration-150 ${
                          selectedImageIndex === index
                            ? "border-indigo-600"
                            : "border-gray-200 hover:border-indigo-300"
                        }`}
                      >
                        <img
                          src={image.url || ""}
                          alt={`View ${index + 1}`}
                          className="h-full w-full object-cover object-center"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="lg:col-span-4 px-6 py-8 lg:px-8 lg:py-10">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {product?.name || selectedVariant?.name}
                </h1>

                <div className="mt-2 flex flex-wrap items-baseline gap-3">
                  <p className="text-3xl font-bold text-red-600">
                    {`Rs. ${formatToDevanagariStyle(
                      selectedVariant?.discount_details?.has_discount
                        ? selectedVariant?.discount_details?.final_price
                        : selectedVariant?.sell_price ||
                            selectedVariant?.price ||
                            product?.price ||
                            0
                    )}`}
                  </p>

                  {selectedVariant?.discount_details?.has_discount ? (
                    <>
                      <p className="text-lg font-medium text-gray-500 line-through">
                        {`Rs. ${formatToDevanagariStyle(
                          selectedVariant?.discount_details?.original_price
                        )}`}
                      </p>

                      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        {selectedVariant?.discount_details?.label}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Variant Selection */}
              {colors.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-900">Color</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {colors.map((color: string) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`px-4 py-2 border rounded-md text-sm font-medium ${
                          selectedColor === color
                            ? "border-indigo-600 bg-indigo-50 text-indigo-600"
                            : "border-gray-300 text-gray-700 hover:border-gray-400"
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {sizes.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-900">Size</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {sizes.map((size: string) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border rounded-md text-sm font-medium ${
                          selectedSize === size
                            ? "border-indigo-600 bg-indigo-50 text-indigo-600"
                            : "border-gray-300 text-gray-700 hover:border-gray-400"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                {selectedVariantAvailableStock > 0 ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    In Stock ({selectedVariantAvailableStock} available)
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    Out of Stock
                  </span>
                )}
              </div>

              {selectedVariant?.sku && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    SKU:{" "}
                    <span className="text-gray-900">{selectedVariant.sku}</span>
                  </p>
                </div>
              )}

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-900">Tags</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {product.tags.map((tag: any) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity and Add to Cart */}
              <div className="mt-8">
                <div className="flex items-center space-x-4 mb-4">
                  <label
                    htmlFor="quantity"
                    className="text-sm font-medium text-gray-700"
                  >
                    Quantity:
                  </label>
                  <div className="flex items-center border rounded-md">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="p-2 hover:bg-gray-100"
                      disabled={quantity <= 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="px-4 py-2 min-w-[60px] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() =>
                        setQuantity((q) =>
                          Math.min(selectedVariantAvailableStock, q + 1)
                        )
                      }
                      className="p-2 hover:bg-gray-100"
                      disabled={quantity >= selectedVariantAvailableStock}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isAdding || selectedVariantAvailableStock === 0}
                  className="w-full bg-indigo-600 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={20} />
                  {isAdding ? "Adding to Cart..." : "Add to Cart"}
                </button>
              </div>

              {/* Description section */}
              {product.description && (
                <div className="mt-8 border-t border-gray-200 pt-8">
                  <h3 className="text-sm font-medium text-gray-900">
                    Description
                  </h3>
                  <p className="mt-4 text-sm text-gray-600">
                    {product.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <Dialog
          fullScreen
          open={fullScreenImageIndex !== null && galleryImages.length > 0}
          onClose={() => setFullScreenImageIndex(null)}
          aria-labelledby="product-gallery-title"
          PaperProps={{ sx: { bgcolor: "#111827", color: "white" } }}
          onKeyDown={(event) => {
            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
              event.preventDefault()
              moveFullScreenImage(event.key === "ArrowRight" ? 1 : -1)
            }
          }}
        >
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between gap-4 p-4">
              <h2 id="product-gallery-title" className="text-base font-medium">
                {product.name}
              </h2>
              <button
                type="button"
                aria-label="Close image gallery"
                onClick={() => setFullScreenImageIndex(null)}
                className="rounded-full p-3 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <X size={24} />
              </button>
            </div>
            <div className="relative flex flex-1 min-h-0 items-center justify-center px-14 sm:px-20">
              {fullScreenImageIndex !== null && (
                <img
                  src={galleryImages[fullScreenImageIndex]?.url || ""}
                  alt={`${product.name} — image ${fullScreenImageIndex + 1}`}
                  className="h-full w-full object-contain"
                />
              )}
              {hasMultipleImages && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={() => moveFullScreenImage(-1)}
                    className="absolute left-2 sm:left-4 rounded-full bg-white/10 p-3 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={() => moveFullScreenImage(1)}
                    className="absolute right-2 sm:right-4 rounded-full bg-white/10 p-3 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>
            <p
              aria-live="polite"
              className="p-4 text-center text-sm text-gray-300"
            >
              {(fullScreenImageIndex ?? 0) + 1} / {galleryImages.length}
            </p>
          </div>
        </Dialog>

        {/* Similar Products */}
        {!isSimilarLoading && similarProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Similar Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {similarProducts.map((p: any) => (
                <ProductGroup product={p} key={p.id} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
